import { useState, useEffect, useRef, FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ThemeToggle } from "@/components/ThemeToggle";
import LandingLanguageFlags from "@/components/LandingLanguageFlags";
import { useTranslation } from "react-i18next";
import {
  ArrowRight, LogIn, Shield, Brain, BarChart3,
  Users, GraduationCap, CalendarDays, CreditCard, FileText,
  Smartphone, Globe, Award, Layers, Bell,
  BookOpen, Target, Activity, Lock, Wifi, ChevronDown,
  Mail, MessageSquare, Star, Zap, Heart, Send, MousePointer2
} from "lucide-react";
import { SEO } from "@/components/SEO";

import heroLogo from "@/assets/landing-hero-logo-clean.png";
import fistbumpImage from "@/assets/landing-fistbump.png";
import beltImage from "@/assets/landing-belt.png";
import futuroImage from "@/assets/landing-futuro.png";
import giImage from "@/assets/landing-gi.png";
import mockupDashboard from "@/assets/mockup-dashboard.jpg";
import mockupMobile from "@/assets/mockup-mobile.jpg";

/* ═══════════════════════════════ STATIC DATA (no i18n needed) ═══════════════════════════════ */

const phrases = [
  { flag: "🇧🇷", hero: "A tecnologia que sua academia merece.", sub: "Gestão completa para academias de Jiu-Jitsu. Do tatame ao financeiro, tudo em um só lugar." },
  { flag: "🇺🇸", hero: "The technology your academy deserves.", sub: "Complete management for Jiu-Jitsu academies. From the mat to finances, all in one place." },
  { flag: "🇪🇸", hero: "La tecnología que tu academia merece.", sub: "Gestión completa para academias de Jiu-Jitsu. Del tatami a las finanzas, todo en un solo lugar." },
  { flag: "🇫🇷", hero: "La technologie que votre académie mérite.", sub: "Gestion complète pour les académies de Jiu-Jitsu. Du tatami aux finances, tout en un seul endroit." },
  { flag: "🇩🇪", hero: "Die Technologie, die Ihre Akademie verdient.", sub: "Komplettes Management für Jiu-Jitsu-Akademien. Von der Matte bis zu den Finanzen, alles an einem Ort." },
  { flag: "🇯🇵", hero: "あなたの道場にふさわしいテクノロジー。", sub: "柔術道場のための完全な管理システム。畳から財務まで、すべてを一つの場所で。" },
];

/* ═══════════════════════════════ COUNT-UP COMPONENT ═══════════════════════════════ */

function CountUp({ target, suffix }: { target: number; suffix: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const duration = 1800;
    const steps = 40;
    const increment = target / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, target]);

  return <span ref={ref}>{count}{suffix}</span>;
}

/* ═══════════════════════════════ ANIMATED ICON ═══════════════════════════════ */

function AnimatedIcon({ icon: Icon, className }: { icon: React.ElementType; className?: string }) {
  return (
    <motion.div
      whileHover={{ rotate: [0, -12, 12, -8, 0], scale: 1.2 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
      className={className}
    >
      <Icon className="w-6 h-6 text-primary" />
    </motion.div>
  );
}

/* ═══════════════════════════════ CONTACT FORM ═══════════════════════════════ */

function ContactForm({ t }: { t: (key: string) => string }) {
  const [loading, setLoading] = useState(false);
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [academiaNome, setAcademiaNome] = useState("");
  const [mensagem, setMensagem] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || !email.trim() || !mensagem.trim()) {
      toast.error(t("landing.fillAllFields"));
      return;
    }
    setLoading(true);
    try {
      const leadId = crypto.randomUUID();
      const trimmedData = {
        nome: nome.trim(),
        email: email.trim(),
        academia_nome: academiaNome.trim() || null,
        mensagem: mensagem.trim(),
      };

      // Save to database
      const { error } = await supabase.from("contact_leads").insert({ ...trimmedData, id: leadId });
      if (error) throw error;

      // Send email to support@submission-bjj.com
      await supabase.functions.invoke("send-app-email", {
        body: {
          templateName: "contact-form",
          recipientEmail: "support@submission-bjj.com",
          idempotencyKey: `contact-form-${leadId}`,
          templateData: {
            nome: trimmedData.nome,
            email: trimmedData.email,
            academiaNome: trimmedData.academia_nome || "",
            mensagem: trimmedData.mensagem,
          },
        },
      });

      toast.success(t("landing.messageSent"));
      setNome(""); setEmail(""); setAcademiaNome(""); setMensagem("");
    } catch {
      toast.error(t("landing.messageError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium text-foreground mb-1.5 block">{t("landing.yourName")}</label>
          <Input placeholder={t("landing.namePlaceholder")} className="bg-background" value={nome} onChange={(e) => setNome(e.target.value)} required />
        </div>
        <div>
          <label className="text-xs font-medium text-foreground mb-1.5 block">{t("landing.emailLabel")}</label>
          <Input type="email" placeholder={t("landing.emailPlaceholder")} className="bg-background" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-foreground mb-1.5 block">{t("landing.academyName")}</label>
        <Input placeholder={t("landing.academyPlaceholder")} className="bg-background" value={academiaNome} onChange={(e) => setAcademiaNome(e.target.value)} />
      </div>
      <div>
        <label className="text-xs font-medium text-foreground mb-1.5 block">{t("landing.message")}</label>
        <Textarea placeholder={t("landing.messagePlaceholder")} rows={4} className="bg-background resize-none" value={mensagem} onChange={(e) => setMensagem(e.target.value)} required />
      </div>
      <Button type="submit" className="w-full bg-primary text-primary-foreground rounded-xl white-gi-weave-btn" disabled={loading}>
        <Send className="w-4 h-4 mr-2" />
        {loading ? "..." : t("landing.sendMessage")}
      </Button>
    </form>
  );
}

/* ═══════════════════════════════ MAIN COMPONENT ═══════════════════════════════ */

const Index = () => {
  const { t } = useTranslation();
  const [phraseIndex, setPhraseIndex] = useState(0);
  const { scrollYProgress } = useScroll();
  const navBg = useTransform(scrollYProgress, [0, 0.05], [0, 1]);

  useEffect(() => {
    const interval = setInterval(() => {
      setPhraseIndex((prev) => (prev + 1) % phrases.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    { icon: Users, title: t("landing.feat_studentMgmt"), desc: t("landing.feat_studentMgmtDesc"), size: "large" },
    { icon: GraduationCap, title: t("landing.feat_smartGraduation"), desc: t("landing.feat_smartGraduationDesc"), size: "small" },
    { icon: Brain, title: t("landing.feat_aiAssistant"), desc: t("landing.feat_aiAssistantDesc"), size: "small" },
    { icon: CreditCard, title: t("landing.feat_financial"), desc: t("landing.feat_financialDesc"), size: "large" },
    { icon: CalendarDays, title: t("landing.feat_classes"), desc: t("landing.feat_classesDesc"), size: "small" },
    { icon: FileText, title: t("landing.feat_contracts"), desc: t("landing.feat_contractsDesc"), size: "small" },
    { icon: BarChart3, title: t("landing.feat_dashboards"), desc: t("landing.feat_dashboardsDesc"), size: "large" },
    { icon: Shield, title: t("landing.feat_lgpd"), desc: t("landing.feat_lgpdDesc"), size: "small" },
    { icon: Globe, title: t("landing.feat_multilingual"), desc: t("landing.feat_multilingualDesc"), size: "small" },
    { icon: Smartphone, title: t("landing.feat_pwa"), desc: t("landing.feat_pwaDesc"), size: "small" },
    { icon: Bell, title: t("landing.feat_notifications"), desc: t("landing.feat_notificationsDesc"), size: "small" },
    { icon: Award, title: t("landing.feat_events"), desc: t("landing.feat_eventsDesc"), size: "small" },
  ];

  const stats = [
    { value: 81, suffix: "+", label: t("landing.stat_tables") },
    { value: 5, suffix: "", label: t("landing.stat_profiles") },
    { value: 6, suffix: "", label: t("landing.stat_languages") },
    { value: 100, suffix: "%", label: t("landing.stat_compliance") },
  ];

  const roles = [
    { icon: Lock, title: t("landing.role_superAdmin"), desc: t("landing.role_superAdminDesc") },
    { icon: Layers, title: t("landing.role_admin"), desc: t("landing.role_adminDesc") },
    { icon: BookOpen, title: t("landing.role_professor"), desc: t("landing.role_professorDesc") },
    { icon: Target, title: t("landing.role_student"), desc: t("landing.role_studentDesc") },
    { icon: Activity, title: t("landing.role_developer"), desc: t("landing.role_developerDesc") },
  ];

  const marqueeItems = [
    "LGPD Compliance", "PGP Encryption", `6 ${t("landing.stat_languages")}`, t("landing.feat_aiAssistant"),
    "PWA & Offline", "81+ Tables", "RLS Security", t("landing.feat_contracts"),
    "CRM", `5 ${t("landing.stat_profiles")}`, "Audit Logs", "SHA-256",
  ];

  const featureTags = [
    { label: t("landing.tagStudents"), desc: t("landing.tagStudentsDesc") },
    { label: t("landing.tagClasses"), desc: t("landing.tagClassesDesc") },
    { label: t("landing.tagFinancial"), desc: t("landing.tagFinancialDesc") },
    { label: t("landing.tagGraduation"), desc: t("landing.tagGraduationDesc") },
    { label: t("landing.tagAI"), desc: t("landing.tagAIDesc") },
    { label: t("landing.tagLGPD"), desc: t("landing.tagLGPDDesc") },
  ];

  const [activeTag, setActiveTag] = useState<number | null>(null);

  return (
    <main className="min-h-screen bg-background">
      <SEO
        title="Submission-BJJ — Gestão completa para academias de Jiu-Jitsu"
        description="SaaS para academias de BJJ: matrículas, contratos digitais, presenças, graduações CBJJ/IBJJF, cobrança recorrente e CRM pedagógico em um só lugar."
        path="/"
      />
      {/* ===== NAV — mobile: compact top bar with flags + icons; desktop: full bar ===== */}
      <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-2xl border-b border-border/30 shadow-[0_1px_20px_rgba(0,0,0,0.03)]" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        {/* Desktop nav */}
        <div className="hidden md:flex max-w-[1200px] mx-auto items-center justify-between h-16 px-6">
          <div className="flex items-center gap-3">
            <img src={heroLogo} alt="Submission BJJ" className="h-10 w-auto" />
            <span className="text-sm font-bold tracking-tight text-foreground">Submission<span className="text-primary">BJJ</span></span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1 mr-2">
              <a href="#features" className="text-xs text-muted-foreground/70 hover:text-foreground px-3 py-1.5 rounded-full hover:bg-muted/50 transition-all duration-200">{t("landing.features")}</a>
              <a href="#contact" className="text-xs text-muted-foreground/70 hover:text-foreground px-3 py-1.5 rounded-full hover:bg-muted/50 transition-all duration-200">{t("landing.contact")}</a>
            </div>
            <div className="w-px h-5 bg-border/50" />
            <LandingLanguageFlags />
            <ThemeToggle />
            <Link to="/login">
              <Button size="sm" className="bg-primary text-primary-foreground text-xs h-8 px-5 rounded-full shadow-[0_2px_16px_hsl(354_76%_50%/0.25)] hover:shadow-[0_4px_24px_hsl(354_76%_50%/0.4)] transition-all duration-300 white-gi-weave-btn">
                {t("landing.enter")}
              </Button>
            </Link>
          </div>
        </div>
        {/* Mobile nav */}
        <div className="flex md:hidden items-center justify-center h-12 px-4 relative">
          <LandingLanguageFlags />
          <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <ThemeToggle />
            <Link to="/login">
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Entrar">
                <LogIn className="h-4 w-4 text-muted-foreground" />
              </Button>
            </Link>
          </div>
        </div>
        {/* Scroll progress bar */}
        <motion.div
          className="absolute bottom-0 left-0 h-[1.5px] bg-gradient-to-r from-primary via-primary/80 to-primary origin-left"
          style={{ scaleX: scrollYProgress }}
        />
      </nav>

      {/* ===== HERO ===== */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-background">
        {/* Premium mesh gradient background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_-10%,hsl(var(--primary)/0.08)_0%,transparent_60%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_40%_60%_at_80%_60%,hsl(var(--primary)/0.04)_0%,transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_40%_at_20%_80%,hsl(var(--primary)/0.03)_0%,transparent_50%)]" />
        </div>
        <div className="absolute inset-0 bjj-texture-header opacity-[0.03]" />

        {/* Floating particles for depth */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute w-1 h-1 rounded-full bg-primary/20"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.6, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 4 + i * 0.8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.7,
            }}
          />
        ))}

        {/* Red glow heartbeat — subtler */}
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/8 blur-[150px]"
          animate={{ scale: [1, 1.12, 1], opacity: [0.15, 0.35, 0.15] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative z-10 max-w-[850px] mx-auto px-4 text-center pt-4 md:pt-20">
          {/* Mobile hero logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="md:hidden flex flex-col items-center mb-3"
          >
            <img src={heroLogo} alt="Submission BJJ Red Line" width={144} height={144} fetchPriority="high" decoding="async" className="h-36 w-auto drop-shadow-[0_4px_24px_rgba(0,0,0,0.12)]" />
          </motion.div>

          {/* Desktop hero logo — elegant, not watermark */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="hidden md:flex justify-center mb-8"
          >
            <img src={heroLogo} alt="Submission BJJ Red Line" width={160} height={80} fetchPriority="high" decoding="async" className="h-20 w-auto drop-shadow-[0_4px_24px_rgba(0,0,0,0.1)]" />
          </motion.div>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-card/60 border border-primary/10 backdrop-blur-xl rounded-full px-5 py-2 mb-6 shadow-[0_2px_20px_rgba(0,0,0,0.06)]"
          >
            <motion.span
              className="w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.5)]"
              animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            />
            <span className="text-[11px] font-semibold text-foreground/60 tracking-[0.2em] uppercase">{t("landing.tagline")}</span>
          </motion.div>

          {/* Rotating hero — bigger, bolder */}
          <h1 className="sr-only">{phrases[phraseIndex].hero}</h1>
          <div className="relative h-[210px] sm:h-[200px] md:h-[220px] mb-6 overflow-hidden" aria-hidden="true">
            {phrases.map((phrase, i) => (
              <motion.div
                key={i}
                initial={false}
                animate={{ opacity: phraseIndex === i ? 1 : 0, y: phraseIndex === i ? 0 : 20, filter: phraseIndex === i ? "blur(0px)" : "blur(4px)" }}
                transition={{ duration: 0.8, ease: "easeInOut" }}
                className="absolute inset-0 flex items-center justify-center text-center px-4 font-display text-3xl sm:text-4xl md:text-[3rem] font-black tracking-tight leading-[1.15] text-foreground"
              >
                {phrase.hero}
              </motion.div>
            ))}
          </div>


          <div className="relative h-[100px] sm:h-[80px] md:h-[70px] mb-8 max-w-[700px] mx-auto overflow-hidden">
            {phrases.map((phrase, i) => (
              <motion.p
                key={i}
                initial={false}
                animate={{ opacity: phraseIndex === i ? 1 : 0, y: phraseIndex === i ? 0 : 10 }}
                transition={{ duration: 0.9, ease: "easeInOut", delay: 0.15 }}
                className="absolute inset-0 flex items-center justify-center text-foreground/55 text-lg md:text-xl text-center px-4 font-medium leading-relaxed"
              >
                {phrase.sub}
              </motion.p>
            ))}
          </div>

          {/* Mobile: dot indicator */}
          <div className="flex md:hidden items-center justify-center gap-1.5 mb-6">
            {phrases.map((_, i) => (
              <motion.span
                key={i}
                layout
                className={cn(
                  "rounded-full transition-all duration-500",
                  phraseIndex === i ? "w-5 h-1.5 bg-primary" : "w-1.5 h-1.5 bg-muted-foreground/20"
                )}
              />
            ))}
          </div>

          {/* CTA buttons */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.6, ease: "easeOut" }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full max-w-xs md:max-w-none mx-auto"
          >
            <Link to="/login" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground rounded-full px-8 shadow-[0_4px_30px_hsl(354_76%_50%/0.4)] hover:shadow-[0_8px_40px_hsl(354_76%_50%/0.55)] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 white-gi-weave-btn">
                <LogIn className="w-4 h-4 mr-2" />
                {t("landing.enterSystem")}
              </Button>
            </Link>
            <a href="#features" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto gap-1 rounded-full border-foreground/15 hover:bg-primary/5 hover:border-primary/30 transition-all duration-300">
                {t("landing.discoverFeatures")} <ChevronDown className="w-4 h-4" />
              </Button>
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.5, delay: 1.2 }}
            className="mt-6 text-muted-foreground/40 text-xs flex items-center justify-center gap-1.5"
          >
            <Lock className="w-3 h-3" /> {t("landing.loginRequired")}
          </motion.p>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            className="flex flex-col items-center gap-0.5"
          >
            <ChevronDown className="w-5 h-5 text-primary/25" />
            <ChevronDown className="w-5 h-5 text-primary/10 -mt-3" />
          </motion.div>
        </motion.div>
      </section>

      {/* ===== MARQUEE TICKER ===== */}
      <section className="py-5 border-y border-border/30 bg-muted/10 overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background z-10 pointer-events-none" />
        <div className="relative flex">
          <motion.div
            className="flex gap-10 whitespace-nowrap"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
          >
            {[...marqueeItems, ...marqueeItems].map((item, i) => (
              <span key={i} className="flex items-center gap-2.5 text-sm text-muted-foreground/50 font-medium tracking-wide">
                <span className="w-1 h-1 rounded-full bg-primary/30" />
                {item}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== TAGLINE + IMAGE SPLIT ===== */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <p className="text-sm text-primary font-semibold tracking-widest uppercase mb-3">{t("landing.futureOfManagement")}</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-4 text-foreground">
                {t("landing.fromMatToFinance")}
                <br />
                {t("landing.professionalManagement")}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {t("landing.heroDescription")}
              </p>
              <div className="flex flex-wrap gap-2.5">
                {featureTags.map((tag, idx) => (
                  <motion.button
                    key={tag.label}
                    onClick={() => setActiveTag(activeTag === idx ? null : idx)}
                    className={cn(
                      "relative px-4 py-2 rounded-lg text-xs font-semibold tracking-wide uppercase transition-all duration-500 cursor-pointer",
                      activeTag === idx
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    whileTap={{ scale: 0.97 }}
                  >
                    {tag.label}
                    {/* Animated underline */}
                    <motion.div
                      className="absolute bottom-0 left-1/2 h-[2px] bg-primary rounded-full"
                      initial={false}
                      animate={{
                        width: activeTag === idx ? "60%" : "0%",
                        x: "-50%",
                        opacity: activeTag === idx ? 1 : 0,
                      }}
                      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                    />
                    {/* Hover glow */}
                    <motion.div
                      className="absolute inset-0 rounded-lg bg-primary/5"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.button>
                ))}
              </div>

              {/* Premium description reveal */}
              <div className="min-h-[80px] mt-6">
                <AnimatePresence mode="wait">
                  {activeTag !== null && (
                    <motion.div
                      key={activeTag}
                      initial={{ opacity: 0, y: 8, filter: "blur(4px)" }}
                      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      exit={{ opacity: 0, y: -4, filter: "blur(4px)" }}
                      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                    >
                      <p className="text-muted-foreground text-sm leading-relaxed pl-1 border-l-2 border-primary/30">
                        {featureTags[activeTag].desc}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
              className="relative"
            >
              <img src={fistbumpImage} alt="Dois lutadores de Jiu-Jitsu" className="rounded-2xl shadow-2xl w-full" />
              <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== PRODUCT MOCKUP ===== */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="max-w-[1100px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center mb-16"
          >
            <p className="text-sm text-primary font-semibold tracking-widest uppercase mb-3">{t("landing.seeTheSystem")}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              {t("landing.professionalInterface")}
            </h2>
            <p className="text-muted-foreground max-w-[600px] mx-auto">
              {t("landing.dashboardDescription")}
            </p>
          </motion.div>

          <div className="relative flex items-end justify-center gap-6 md:gap-10">
            {/* Laptop mockup */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="relative w-full max-w-[750px]"
            >
              <div className="relative rounded-t-xl border-[3px] border-foreground/20 bg-foreground/10 p-1.5 shadow-2xl">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-1 rounded-b-full bg-foreground/15" />
                <img 
                  src={mockupDashboard} 
                  alt="Dashboard Submission BJJ" 
                  loading="lazy" 
                  width={1920} 
                  height={1080} 
                  className="rounded-lg w-full" 
                />
              </div>
              <div className="h-3 bg-foreground/15 rounded-b-lg mx-8" />
              <div className="h-1.5 bg-foreground/10 rounded-b-2xl mx-16" />
            </motion.div>

            {/* Mobile mockup */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
              className="absolute right-2 md:right-4 bottom-4 w-[110px] md:w-[150px] z-10"
            >
              <div className="rounded-[20px] border-[3px] border-foreground/20 bg-foreground/10 p-1 shadow-2xl">
                <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-foreground/15" />
                <img 
                  src={mockupMobile} 
                  alt="App mobile Submission BJJ" 
                  loading="lazy" 
                  width={512} 
                  height={1024} 
                  className="rounded-[16px] w-full" 
                />
              </div>
            </motion.div>

            {/* Floating glow */}
            <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[80%] h-[60px] bg-primary/20 blur-[60px] rounded-full" />
          </div>
        </div>
      </section>

      {/* ===== BENTO FEATURES GRID ===== */}
      <section id="features" className="py-20 md:py-28 bg-muted/30 relative overflow-hidden scroll-mt-16">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[900px] h-[600px] rounded-full opacity-[0.06]" style={{ background: "radial-gradient(circle, hsl(354 76% 50% / 0.4) 0%, transparent 70%)" }} />
        </div>
        <div className="relative max-w-[1100px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center mb-16"
          >
            <p className="text-sm text-primary font-semibold tracking-widest uppercase mb-3">{t("landing.features")}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
              {t("landing.allYourAcademyNeeds")}
            </h2>
            <p className="text-muted-foreground max-w-[600px] mx-auto">
              {t("landing.completePlatform")}
            </p>
          </motion.div>

          {/* Premium Bento Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.05, duration: 0.7, ease: "easeOut" }}
                className={`group relative rounded-2xl border border-border/60 bg-card overflow-hidden transition-all duration-500 cursor-default white-gi-weave ${
                  f.size === "large" ? "lg:col-span-2" : ""
                }`}
              >
                {/* Hover gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-primary/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                
                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                
                <div className="relative p-6 md:p-7">
                  {/* Minimal icon */}
                  <div className="w-10 h-10 rounded-lg bg-primary/[0.15] group-hover:bg-primary/[0.22] transition-all duration-500 flex items-center justify-center mb-5">
                    <f.icon className="w-[18px] h-[18px] text-primary group-hover:text-primary transition-colors duration-500 bjj-embroidered-icon" />
                  </div>
                  
                  <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-2.5 text-foreground/90 group-hover:text-foreground transition-colors duration-500 bjj-embroidered">
                    {f.title}
                  </h3>
                  
                  <p className="text-muted-foreground/70 text-xs leading-relaxed group-hover:text-muted-foreground transition-colors duration-500 bjj-embroidered">
                    {f.desc}
                  </p>
                </div>
                
                {/* Bottom subtle border glow on hover */}
                <div className="absolute bottom-0 left-[20%] right-[20%] h-[1px] bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== STATS BAND — Count-up ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={futuroImage} alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/80" />
          <div className="absolute inset-0 bjj-texture-header opacity-30" />
        </div>
        <div className="relative py-16 md:py-28 max-w-[900px] mx-auto px-6 text-center text-white">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="font-display text-xl sm:text-2xl md:text-4xl font-bold mb-10 md:mb-16 leading-tight"
          >
            {t("landing.builtForBjj")}
          </motion.h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.12, duration: 0.8, ease: "easeOut" }}
                className="text-center bg-black/40 backdrop-blur-sm rounded-xl py-4 px-2 white-gi-weave-kpi"
              >
                <div className="font-display text-3xl sm:text-4xl md:text-5xl font-bold mb-1 text-primary">
                  <CountUp target={s.value} suffix={s.suffix} />
                </div>
                <p className="text-white/60 text-xs sm:text-sm">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== ROLES ===== */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center mb-16"
          >
            <p className="text-sm text-primary font-semibold tracking-widest uppercase mb-3">{t("landing.accessProfiles")}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{t("landing.eachUserInPlace")}</h2>
            <p className="text-muted-foreground max-w-[600px] mx-auto">
              {t("landing.fiveProfiles")}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-12">
            {roles.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.05, duration: 0.7, ease: "easeOut" }}
                className="group relative rounded-2xl border border-border/60 bg-card overflow-hidden transition-all duration-500 cursor-default bjj-texture-light"
              >
                {/* Hover gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-primary/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                
                {/* Top accent line */}
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                
                <div className="relative p-6 md:p-7">
                  <div className="w-10 h-10 rounded-lg bg-primary/[0.15] group-hover:bg-primary/[0.22] transition-all duration-500 flex items-center justify-center mb-5">
                    <r.icon className="w-[18px] h-[18px] text-primary group-hover:text-primary transition-colors duration-500 bjj-embroidered-icon" />
                  </div>
                  
                  <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-2.5 text-foreground/90 group-hover:text-foreground transition-colors duration-500 bjj-embroidered">
                    {r.title}
                  </h3>
                  
                  <p className="text-muted-foreground/70 text-xs leading-relaxed group-hover:text-muted-foreground transition-colors duration-500 bjj-embroidered">
                    {r.desc}
                  </p>
                </div>
                
                {/* Bottom subtle border glow on hover */}
                <div className="absolute bottom-0 left-[20%] right-[20%] h-[1px] bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </motion.div>
            ))}
          </div>

          {/* Belt & Gi showcase */}
          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="overflow-hidden rounded-2xl"
            >
              <img src={beltImage} alt="Faixa preta Submission BJJ" className="w-full h-64 object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ delay: 0.15, duration: 0.8, ease: "easeOut" }}
              className="overflow-hidden rounded-2xl"
            >
              <img src={giImage} alt="Kimono Submission BJJ Red Line" className="w-full h-64 object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== SECURITY & COMPLIANCE ===== */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="max-w-[1000px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center mb-16"
          >
            <p className="text-sm text-primary font-semibold tracking-widest uppercase mb-3">{t("landing.security")}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{t("landing.dataProtected")}</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
            {[
              { icon: Lock, title: t("landing.sec_pgp"), desc: t("landing.sec_pgpDesc") },
              { icon: Shield, title: t("landing.sec_rls"), desc: t("landing.sec_rlsDesc") },
              { icon: FileText, title: t("landing.sec_audit"), desc: t("landing.sec_auditDesc") },
              { icon: Wifi, title: t("landing.sec_offline"), desc: t("landing.sec_offlineDesc") },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.05, duration: 0.7, ease: "easeOut" }}
                className="group relative rounded-2xl border border-border/60 bg-card overflow-hidden transition-all duration-500 cursor-default bjj-texture-light"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-primary/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                
                <div className="relative p-6 md:p-7 text-center">
                  <div className="w-10 h-10 rounded-lg bg-primary/[0.15] group-hover:bg-primary/[0.22] transition-all duration-500 flex items-center justify-center mx-auto mb-5">
                    <item.icon className="w-[18px] h-[18px] text-primary group-hover:text-primary transition-colors duration-500 bjj-embroidered-icon" />
                  </div>
                  
                  <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-2.5 text-foreground/90 group-hover:text-foreground transition-colors duration-500 bjj-embroidered">
                    {item.title}
                  </h3>
                  
                  <p className="text-muted-foreground/70 text-xs leading-relaxed group-hover:text-muted-foreground transition-colors duration-500 bjj-embroidered">
                    {item.desc}
                  </p>
                </div>
                
                <div className="absolute bottom-0 left-[20%] right-[20%] h-[1px] bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF ===== */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center mb-16"
          >
            <p className="text-sm text-primary font-semibold tracking-widest uppercase mb-3">{t("landing.whyChooseUs")}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{t("landing.madeByExperts")}</h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-3 md:gap-4">
            {[
              { icon: Zap, title: t("landing.proof_setup"), desc: t("landing.proof_setupDesc"), highlight: t("landing.proof_setupHighlight") },
              { icon: Heart, title: t("landing.proof_support"), desc: t("landing.proof_supportDesc"), highlight: t("landing.proof_supportHighlight") },
              { icon: Star, title: t("landing.proof_evolution"), desc: t("landing.proof_evolutionDesc"), highlight: t("landing.proof_evolutionHighlight") },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.05, duration: 0.7, ease: "easeOut" }}
                className="group relative rounded-2xl border border-border/60 bg-card overflow-hidden transition-all duration-500 cursor-default bjj-texture-light"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-primary/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                
                <div className="relative p-6 md:p-7">
                  <span className="absolute top-6 right-6 text-[9px] font-semibold uppercase tracking-widest text-primary/60 group-hover:text-primary transition-colors duration-500">
                    {item.highlight}
                  </span>
                  
                  <div className="w-10 h-10 rounded-lg bg-primary/[0.15] group-hover:bg-primary/[0.22] transition-all duration-500 flex items-center justify-center mb-5">
                    <item.icon className="w-[18px] h-[18px] text-primary group-hover:text-primary transition-colors duration-500 bjj-embroidered-icon" />
                  </div>
                  
                  <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-2.5 text-foreground/90 group-hover:text-foreground transition-colors duration-500 bjj-embroidered">
                    {item.title}
                  </h3>
                  
                  <p className="text-muted-foreground/70 text-xs leading-relaxed group-hover:text-muted-foreground transition-colors duration-500 bjj-embroidered">
                    {item.desc}
                  </p>
                </div>
                
                <div className="absolute bottom-0 left-[20%] right-[20%] h-[1px] bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute -top-32 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[hsl(0_0%_5%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[hsl(0_0%_5%)]" />
        <div className="absolute inset-0 bjj-texture-header opacity-15" />
        <motion.div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-primary/10 blur-[120px]"
          animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="relative max-w-[700px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            <motion.div
              animate={{ scale: [1, 1.05, 1, 1.06, 1] }}
              transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 0.8, times: [0, 0.15, 0.3, 0.45, 0.6] }}
              className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6"
            >
              <Heart className="w-8 h-8 text-primary" />
            </motion.div>
            <h2 className="font-display text-3xl md:text-4xl font-bold mb-4 text-white">
              {t("landing.readyToElevate")}
            </h2>
            <p className="text-white/60 text-lg mb-8">
              {t("landing.joinAcademies")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/login">
                <Button size="lg" className="bg-primary text-primary-foreground rounded-full px-10 shadow-[0_0_40px_hsl(354_76%_50%/0.5)] hover:shadow-[0_0_60px_hsl(354_76%_50%/0.6)] text-base">
                  {t("landing.startNow")} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="#contact">
                <Button size="lg" variant="ghost" className="rounded-full px-8 text-white border border-white/20 hover:bg-white/10">
                  {t("landing.talkToUs")}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== CONTACT — with form ===== */}
      <section id="contact" className="py-20 md:py-28 bg-muted/30 scroll-mt-16">
        <div className="max-w-[900px] mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="text-center mb-12">
              <p className="text-sm text-primary font-semibold tracking-widest uppercase mb-3">{t("landing.contact")}</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">{t("landing.talkWithUs")}</h2>
              <p className="text-muted-foreground">{t("landing.contactDescription")}</p>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {/* Form */}
              <div className="md:col-span-3">
                <ContactForm t={t} />
              </div>

              {/* Contact info */}
              <div className="md:col-span-2 flex flex-col gap-4 justify-center">
                {[
                  { icon: Mail, title: t("landing.emailLabel"), content: "support@submission-bjj.com", href: "mailto:support@submission-bjj.com", external: false },
                  { icon: MessageSquare, title: t("landing.whatsapp"), content: t("landing.talkViaWhatsapp"), href: "https://wa.me/5511999999999", external: true },
                ].map((item, i) => (
                  <a
                    key={i}
                    href={item.href}
                    target={item.external ? "_blank" : undefined}
                    rel={item.external ? "noopener noreferrer" : undefined}
                    className="group relative rounded-2xl border border-border/60 bg-card overflow-hidden transition-all duration-500 block bjj-texture-light"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.04] via-transparent to-primary/[0.02] opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                    <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/40 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
                    
                    <div className="relative p-6">
                      <div className="w-10 h-10 rounded-lg bg-primary/[0.15] group-hover:bg-primary/[0.22] transition-all duration-500 flex items-center justify-center mb-4">
                        <item.icon className="w-[18px] h-[18px] text-primary group-hover:text-primary transition-colors duration-500 bjj-embroidered-icon" />
                      </div>
                      
                      <h3 className="font-display text-sm font-bold uppercase tracking-wide mb-1.5 text-foreground/90 group-hover:text-foreground transition-colors duration-500 bjj-embroidered">
                        {item.title}
                      </h3>
                      
                      <p className="text-muted-foreground/70 text-xs group-hover:text-muted-foreground transition-colors duration-500 bjj-embroidered">
                        {item.content}
                      </p>
                    </div>
                    
                    <div className="absolute bottom-0 left-[20%] right-[20%] h-[1px] bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="relative py-14 border-t border-border/50 bg-card/30 backdrop-blur-sm">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,hsl(var(--primary)/0.03)_0%,transparent_60%)]" />
        <div className="relative max-w-[1200px] mx-auto px-6">
          <div className="grid sm:grid-cols-3 gap-10 mb-10">
            <div className="sm:col-span-1">
              <div className="flex items-center gap-2.5 mb-4">
                <img src={heroLogo} alt="Submission BJJ" className="h-9 w-auto" />
                <span className="text-sm font-bold tracking-tight">Submission<span className="text-primary">BJJ</span></span>
              </div>
              <p className="text-muted-foreground/70 text-xs leading-relaxed mb-4">
                {t("landing.footerDescription")}
              </p>
              <div className="flex items-center gap-3">
                <a href="mailto:support@submission-bjj.com" className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300">
                  <Mail className="w-3.5 h-3.5" />
                </a>
                <a href="https://wa.me/5511999999999" className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-300">
                  <MessageSquare className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-xs uppercase tracking-wider text-foreground/80 mb-4">{t("landing.product")}</h4>
              <ul className="space-y-2.5 text-xs text-muted-foreground/70">
                <li><a href="#features" className="hover:text-foreground transition-colors duration-200">{t("landing.features")}</a></li>
                <li><a href="#features" className="hover:text-foreground transition-colors duration-200">{t("landing.security")}</a></li>
                <li><a href="#contact" className="hover:text-foreground transition-colors duration-200">{t("landing.contact")}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-xs uppercase tracking-wider text-foreground/80 mb-4">{t("landing.legal")}</h4>
              <ul className="space-y-2.5 text-xs text-muted-foreground/70">
                <li><Link to="/politica-privacidade" className="hover:text-foreground transition-colors duration-200">{t("landing.privacyPolicy")}</Link></li>
                <li><Link to="/termos-de-uso" className="hover:text-foreground transition-colors duration-200">{t("landing.termsOfUse")}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border/30 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-muted-foreground/50 text-[11px]">
              © {new Date().getFullYear()} Submission BJJ. {t("landing.allRightsReserved")}
            </p>
            <p className="text-muted-foreground/30 text-[10px] tracking-wider uppercase">
              Red Line System
            </p>
          </div>
        </div>
      </footer>
    </main>
  );
};

export default Index;
